import { shade, tint, transparentize } from 'polished'
import base, { baseBorderRadii } from './base'
import { neutralControlPalette, neutralScrimPalette } from './neutralOverlayPalettes'
import { BLUE, BRONZE, GOLD, GREY, INFINITE, LIME_GREEN, ORANGE, PLATINUM, PURPLE, RED, TEAL } from './primitiveColours'
import { baseThemeShadows } from './shadows'

const brandPalette: Record<App.Design.PaletteType, App.Design.BrandPalette<string>> = {
  default: {
    primary: {
      normal: GREY[800],
      darkest: shade(0.24, '#363A45'),
      dark: shade(0.16, '#363A45'),
      light: tint(0.76, '#363A45'),
      lightest: tint(0.86, '#363A45'),
      hover: tint(0.16, '#363A45'),
      active: tint(0.24, '#363A45'),
    },
  },
  inverse: {
    primary: {
      normal: '#FFFFFF',
      darkest: shade(0.24, '#FFFFFF'),
      dark: shade(0.16, '#FFFFFF'),
      light: tint(0.76, '#FFFFFF'),
      lightest: tint(0.86, '#FFFFFF'),
      hover: tint(0.16, '#FFFFFF'),
      active: tint(0.24, '#FFFFFF'),
    },
  },
}

const neutralPalette: Record<App.Design.PaletteType, App.Design.NeutralPalette<string>> = {
  default: {
    default: {
      one: GREY[800],
      two: GREY[700],
      three: GREY[500],
      four: GREY[300],
      five: GREY[200],
      six: GREY[100],
      seven: GREY[50],
      eight: GREY[0],
      clear: transparentize(0.84, GREY[0]),
    },
    surface: {
      normalForeground: GREY[0],
      normalBackground: GREY[0],
      normalContrast: GREY[800],
      lightForeground: GREY[200],
      lightBackground: GREY[50],
      lightContrast: GREY[800],
      lightForegroundAlt: GREY[700],
      lightBackgroundAlt: GREY[400],
      lightContrastAlt: GREY[800],
      darkForeground: GREY[800],
      darkBackground: GREY[800],
      darkContrast: GREY[0],
      staticWhite: GREY[0],
    },
    control: neutralControlPalette.default,
    scrim: neutralScrimPalette,
  },
  inverse: {
    default: {
      one: GREY[0],
      two: GREY[50],
      three: GREY[100],
      four: GREY[200],
      five: GREY[300],
      six: GREY[500],
      seven: GREY[700],
      eight: GREY[800],
      clear: transparentize(0.84, GREY[0]),
    },
    surface: {
      normalForeground: GREY[800],
      normalBackground: GREY[800],
      normalContrast: GREY[0],
      lightForeground: GREY[600],
      lightBackground: GREY[700],
      lightContrast: GREY[0],
      lightForegroundAlt: GREY[200],
      lightBackgroundAlt: GREY[400],
      lightContrastAlt: GREY[800],
      darkForeground: GREY[0],
      darkBackground: GREY[0],
      darkContrast: GREY[800],
      staticWhite: GREY[0],
    },
    control: neutralControlPalette.inverse,
    scrim: neutralScrimPalette,
  },
}

const productPalette: Record<App.Design.PaletteType, App.Design.ProductPalette<string>> = {
  default: {
    exclusive: {
      foreground: GOLD[600],
      background: GOLD[600],
      contrast: GREY[0],
    },
    escape: {
      foreground: RED[600],
      background: RED[500],
      contrast: GREY[0],
    },
    partner: {
      foreground: GREY[600],
      background: GREY[600],
      contrast: GREY[0],
    },
    premium: {
      foreground: GREY[700],
      background: GREY[700],
      contrast: GREY[0],
    },
    ultralux: {
      foreground: GOLD[600],
      background: GOLD[600],
      contrast: GREY[0],
      active: shade(0.24, GOLD[600]),
      hover: shade(0.16, GOLD[600]),
    },
    luxPlus: {
      foreground: BLUE[700],
      altForeground: BLUE[700],
      background: BLUE[700],
      contrast: GREY[0],
      active: shade(0.24, BLUE[700]),
      altActive: shade(0.24, BLUE[700]),
      hover: shade(0.16, BLUE[700]),
      altHover: shade(0.16, BLUE[700]),
    },
    ai: {
      gradient: `linear-gradient(180deg, ${TEAL[100]} 0%, ${TEAL[600]} 100%)`,
      light: TEAL[100],
      light35: transparentize(0.65, TEAL[100]),
      dark: TEAL[600],
      dark25: transparentize(0.75, TEAL[600]),
      contrast: GREY[800],
    },
    loyalty: {
      bronze: {
        gradientPrimaryBackground: `linear-gradient(45deg, ${BRONZE[300]} 0.73%, ${BRONZE[100]} 45.77%, ${BRONZE[300]} 74.49%)`,
        gradientSecondaryBackground: `linear-gradient(270deg, ${BRONZE[100]} 0%, ${BRONZE[300]} 100%)`,
        gradientTertiaryBackground: '',
        gradientTertiaryContrast: '',
        darkBackground: BRONZE[600],
        darkContrast: GREY[0],
        darkForeground: BRONZE[600],
        lightBackground: BRONZE[100],
        lightContrast: BRONZE[700],
        lightForeground: BRONZE[100],
        normalBackground: BRONZE[300],
        normalContrast: GREY[950],
        normalForeground: BRONZE[300],
      },
      silver: {
        gradientPrimaryBackground: `linear-gradient(45deg, ${GREY[300]} 0.73%, ${GREY[100]} 45.77%, ${GREY[300]} 74.49%)`,
        gradientSecondaryBackground: `linear-gradient(270deg, ${GREY[100]} 0%, ${GREY[300]} 100%)`,
        gradientTertiaryBackground: '',
        gradientTertiaryContrast: '',
        darkBackground: GREY[600],
        darkContrast: GREY[0],
        darkForeground: GREY[600],
        lightBackground: GREY[100],
        lightContrast: GREY[700],
        lightForeground: GREY[100],
        normalBackground: GREY[300],
        normalContrast: GREY[950],
        normalForeground: GREY[300],
      },
      gold: {
        gradientPrimaryBackground: `linear-gradient(45deg, ${GOLD[300]} 0.73%, ${GOLD[100]} 45.77%, ${GOLD[300]} 74.49%)`,
        gradientSecondaryBackground: `linear-gradient(270deg, ${GOLD[100]} 0%, ${GOLD[300]} 100%)`,
        gradientTertiaryBackground: '',
        gradientTertiaryContrast: '',
        darkBackground: GOLD[600],
        darkContrast: GREY[0],
        darkForeground: GOLD[600],
        lightBackground: GOLD[100],
        lightContrast: GOLD[700],
        lightForeground: GOLD[100],
        normalBackground: GOLD[300],
        normalContrast: GREY[950],
        normalForeground: GOLD[300],
      },
      platinum: {
        gradientPrimaryBackground: `linear-gradient(45deg, ${PLATINUM[900]} 0.73%, ${PLATINUM[600]} 45.77%, ${PLATINUM[900]} 74.49%)`,
        gradientSecondaryBackground: `linear-gradient(270deg, ${PLATINUM[600]} 0%, ${PLATINUM[900]} 100%)`,
        gradientTertiaryBackground: '',
        gradientTertiaryContrast: '',
        normalForeground: PLATINUM[600],
        normalBackground: PLATINUM[600],
        normalContrast: GREY[0],
        lightForeground: PLATINUM[100],
        lightBackground: PLATINUM[100],
        lightContrast: PLATINUM[700],
        darkForeground: PLATINUM[700],
        darkBackground: PLATINUM[700],
        darkContrast: GREY[0],
      },
      infinite: {
        gradientPrimaryBackground: `linear-gradient(45deg, ${INFINITE[950]} 0.73%, ${INFINITE[700]} 45.77%, ${INFINITE[950]} 74.49%)`,
        gradientSecondaryBackground: `linear-gradient(270deg, ${INFINITE[700]} 0%, ${INFINITE[950]} 100%)`,
        gradientTertiaryBackground: `linear-gradient(45deg, ${INFINITE[300]} 0.73%, ${INFINITE[100]} 45.77%, ${INFINITE[300]} 74.49%)`,
        gradientTertiaryContrast: INFINITE[800],
        normalForeground: INFINITE[600],
        normalBackground: INFINITE[600],
        normalContrast: INFINITE[0],
        lightForeground: INFINITE[100],
        lightBackground: INFINITE[100],
        lightContrast: INFINITE[800],
        darkForeground: INFINITE[800],
        darkBackground: INFINITE[800],
        darkContrast: INFINITE[0],
      },
    },
    flights: {
      seats: {
        standard: {
          normalForeground: ORANGE[500],
          normalBackground: ORANGE[500],
          normalContrast: GREY[800],
          lightForeground: ORANGE[50],
          lightBackground: ORANGE[50],
          lightContrast: ORANGE[800],
        },
        upfront: {
          normalForeground: BLUE[500],
          normalBackground: BLUE[500],
          normalContrast: GREY[0],
          lightForeground: BLUE[50],
          lightBackground: BLUE[50],
          lightContrast: BLUE[700],
        },
        legroom: {
          normalForeground: PURPLE[500],
          normalBackground: PURPLE[500],
          normalContrast: GREY[0],
          lightForeground: PURPLE[50],
          lightBackground: PURPLE[50],
          lightContrast: PURPLE[700],
        },
      },
    },
  },
  inverse: {
    exclusive: {
      foreground: GOLD[300],
      background: GOLD[500],
      contrast: GREY[0],
    },
    escape: {
      foreground: RED[200],
      background: RED[500],
      contrast: GREY[0],
    },
    partner: {
      foreground: GREY[200],
      background: GREY[500],
      contrast: GREY[0],
    },
    premium: {
      foreground: GREY[300],
      background: GREY[700],
      contrast: GREY[0],
    },
    ultralux: {
      foreground: GOLD[300],
      background: GOLD[500],
      contrast: GREY[0],
      active: shade(0.24, GOLD[500]),
      hover: shade(0.16, GOLD[500]),
    },
    luxPlus: {
      foreground: BLUE[300],
      altForeground: GREY[0],
      background: BLUE[700],
      contrast: GREY[0],
      active: shade(0.24, BLUE[700]),
      altActive: shade(0.24, GREY[0]),
      hover: shade(0.16, BLUE[700]),
      altHover: shade(0.16, GREY[0]),
    },
    ai: {
      gradient: `linear-gradient(180deg, ${TEAL[100]} 0%, ${TEAL[600]} 100%)`,
      light: TEAL[100],
      light35: transparentize(0.65, TEAL[100]),
      dark: TEAL[600],
      dark25: transparentize(0.75, TEAL[600]),
      contrast: GREY[0],
    },
    loyalty: {
      bronze: {
        gradientPrimaryBackground: `linear-gradient(45deg, ${BRONZE[300]} 0.73%, ${BRONZE[100]} 45.77%, ${BRONZE[300]} 74.49%)`,
        gradientSecondaryBackground: `linear-gradient(270deg, ${BRONZE[300]} 0, ${BRONZE[100]} 100%)`,
        gradientTertiaryBackground: '',
        gradientTertiaryContrast: '',
        darkBackground: BRONZE[500],
        darkContrast: GREY[0],
        darkForeground: BRONZE[200],
        lightBackground: BRONZE[100],
        lightContrast: BRONZE[600],
        lightForeground: BRONZE[100],
        normalBackground: BRONZE[300],
        normalContrast: GREY[950],
        normalForeground: BRONZE[300],
      },
      silver: {
        gradientPrimaryBackground: `linear-gradient(45deg, ${GREY[300]} 0.73%, ${GREY[100]} 45.77%, ${GREY[300]} 74.49%)`,
        gradientSecondaryBackground: `linear-gradient(270deg, ${GREY[300]} 0, ${GREY[100]} 100%)`,
        gradientTertiaryBackground: '',
        gradientTertiaryContrast: '',
        darkBackground: GREY[500],
        darkContrast: GREY[0],
        darkForeground: GREY[200],
        lightBackground: GREY[100],
        lightContrast: GREY[600],
        lightForeground: GREY[100],
        normalBackground: GREY[300],
        normalContrast: GREY[950],
        normalForeground: GREY[300],
      },
      gold: {
        gradientPrimaryBackground: `linear-gradient(45deg, ${GOLD[300]} 0.73%, ${GOLD[100]} 45.77%, ${GOLD[300]} 74.49%)`,
        gradientSecondaryBackground: `linear-gradient(270deg, ${GOLD[300]} 0, ${GOLD[100]} 100%)`,
        gradientTertiaryBackground: '',
        gradientTertiaryContrast: '',
        darkBackground: GOLD[400],
        darkContrast: GREY[0],
        darkForeground: GOLD[200],
        lightBackground: GOLD[100],
        lightContrast: GOLD[600],
        lightForeground: GOLD[100],
        normalBackground: GOLD[300],
        normalContrast: GREY[950],
        normalForeground: GOLD[300],
      },
      platinum: {
        gradientPrimaryBackground: `linear-gradient(45deg, ${PLATINUM[900]} 0.73%, ${PLATINUM[600]} 45.77%, ${PLATINUM[900]} 74.49%)`,
        gradientSecondaryBackground: `linear-gradient(270deg, ${PLATINUM[700]} 0, ${PLATINUM[500]} 100%)`,
        gradientTertiaryBackground: '',
        gradientTertiaryContrast: '',
        normalForeground: PLATINUM[300],
        normalBackground: PLATINUM[500],
        normalContrast: GREY[0],
        lightForeground: PLATINUM[200],
        lightBackground: PLATINUM[400],
        lightContrast: GREY[950],
        darkForeground: PLATINUM[400],
        darkBackground: PLATINUM[950],
        darkContrast: GREY[100],
      },
      infinite: {
        gradientPrimaryBackground: `linear-gradient(45deg, ${INFINITE[950]} 0.73%, ${INFINITE[700]} 45.77%, ${INFINITE[950]} 74.49%)`,
        gradientSecondaryBackground: `linear-gradient(270deg, ${INFINITE[950]} 0%, ${INFINITE[700]} 100%)`,
        gradientTertiaryBackground: `linear-gradient(45deg, ${INFINITE[300]} 0.73%, ${INFINITE[100]} 45.77%, ${INFINITE[300]} 74.49%)`,
        gradientTertiaryContrast: INFINITE[800],
        normalForeground: INFINITE[600],
        normalBackground: INFINITE[600],
        normalContrast: INFINITE[0],
        lightForeground: INFINITE[800],
        lightBackground: INFINITE[800],
        lightContrast: INFINITE[0],
        darkForeground: INFINITE[100],
        darkBackground: INFINITE[100],
        darkContrast: INFINITE[800],
      },
    },
    flights: {
      seats: {
        standard: {
          normalForeground: ORANGE[500],
          normalBackground: ORANGE[500],
          normalContrast: GREY[800],
          lightForeground: ORANGE[900],
          lightBackground: ORANGE[900],
          lightContrast: ORANGE[50],
        },
        upfront: {
          normalForeground: BLUE[500],
          normalBackground: BLUE[500],
          normalContrast: GREY[0],
          lightForeground: BLUE[900],
          lightBackground: BLUE[900],
          lightContrast: BLUE[100],
        },
        legroom: {
          normalForeground: PURPLE[500],
          normalBackground: PURPLE[500],
          normalContrast: GREY[0],
          lightForeground: PURPLE[900],
          lightBackground: PURPLE[900],
          lightContrast: PURPLE[50],
        },
      },
    },
  },
}

const messagingPalette: Record<App.Design.PaletteType, App.Design.MessagingPalette<string>> = {
  default: {
    success: {
      normalForeground: LIME_GREEN[500],
      normalBackground: LIME_GREEN[500],
      normalContrast: GREY[900],
      lightForeground: LIME_GREEN[50],
      lightBackground: LIME_GREEN[50],
      lightContrast: GREY[800],
      darkForeground: LIME_GREEN[700],
      darkBackground: LIME_GREEN[700],
      darkContrast: GREY[0],
    },
    warning: {
      normalForeground: ORANGE[500],
      normalBackground: ORANGE[500],
      normalContrast: GREY[800],
      lightForeground: ORANGE[100],
      lightBackground: ORANGE[100],
      lightContrast: GREY[800],
      darkForeground: ORANGE[700],
      darkBackground: ORANGE[700],
      darkContrast: GREY[0],
    },
    urgency: {
      normalForeground: RED[600],
      normalBackground: RED[600],
      normalContrast: GREY[0],
      lightForeground: RED[200],
      lightBackground: RED[200],
      lightContrast: GREY[800],
      darkForeground: RED[700],
      darkBackground: RED[700],
      darkContrast: GREY[0],
    },
    critical: {
      normalForeground: RED[600],
      normalBackground: RED[600],
      normalContrast: GREY[0],
      lightForeground: RED[200],
      lightBackground: RED[200],
      lightContrast: GREY[800],
      darkForeground: RED[700],
      darkBackground: RED[700],
      darkContrast: GREY[0],
    },
    favourite: {
      normalForeground: RED[600],
      normalBackground: RED[600],
      normalContrast: GREY[0],
      lightForeground: RED[200],
      lightBackground: RED[200],
      lightContrast: GREY[800],
      darkForeground: RED[700],
      darkBackground: RED[700],
      darkContrast: GREY[0],
    },
  },
  inverse: {
    success: {
      normalForeground: LIME_GREEN[500],
      normalBackground: LIME_GREEN[500],
      normalContrast: GREY[0],
      lightForeground: LIME_GREEN[50],
      lightBackground: LIME_GREEN[50],
      lightContrast: GREY[800],
      darkForeground: LIME_GREEN[600],
      darkBackground: LIME_GREEN[600],
      darkContrast: GREY[0],
    },
    warning: {
      normalForeground: ORANGE[500],
      normalBackground: ORANGE[500],
      normalContrast: GREY[800],
      lightForeground: ORANGE[300],
      lightBackground: ORANGE[100],
      lightContrast: GREY[800],
      darkForeground: ORANGE[600],
      darkBackground: ORANGE[600],
      darkContrast: GREY[800],
    },
    urgency: {
      normalForeground: RED[500],
      normalBackground: RED[500],
      normalContrast: GREY[0],
      lightForeground: RED[200],
      lightBackground: RED[200],
      lightContrast: GREY[800],
      darkForeground: RED[600],
      darkBackground: RED[600],
      darkContrast: GREY[0],
    },
    critical: {
      normalForeground: RED[500],
      normalBackground: RED[500],
      normalContrast: GREY[0],
      lightForeground: RED[200],
      lightBackground: RED[200],
      lightContrast: GREY[800],
      darkForeground: RED[600],
      darkBackground: RED[600],
      darkContrast: GREY[0],
    },
    favourite: {
      normalForeground: RED[500],
      normalBackground: RED[500],
      normalContrast: GREY[0],
      lightForeground: RED[200],
      lightBackground: RED[200],
      lightContrast: GREY[800],
      darkForeground: RED[600],
      darkBackground: RED[600],
      darkContrast: GREY[0],
    },
  },
}

const legacyDefaultHighlightSecondary = {
  normalForeground: PURPLE[500],
  normalBackground: PURPLE[500],
  normalContrast: GREY[0],
  lightForeground: PURPLE[50],
  lightBackground: PURPLE[50],
  lightContrast: GREY[800],
}

const legacyInverseHighlightSecondary = {
  normalForeground: PURPLE[500],
  normalBackground: PURPLE[500],
  normalContrast: GREY[0],
  lightForeground: PURPLE[500],
  lightBackground: PURPLE[50],
  lightContrast: PURPLE[500],
}

const highlightPalette: Record<App.Design.PaletteType, App.Design.HighlightPalette<string>> = {
  default: {
    primary: {
      normalForeground: TEAL[200],
      normalBackground: TEAL[200],
      normalContrast: GREY[800],
      normalActive: shade(0.24, TEAL[200]),
      normalHover: shade(0.16, TEAL[200]),
      lightForeground: TEAL[50],
      lightBackground: TEAL[50],
      lightContrast: GREY[800],
      darkForeground: TEAL[600],
      darkBackground: TEAL[600],
      darkContrast: GREY[0],
      darkActive: shade(0.24, TEAL[600]),
    },
    secondary: legacyDefaultHighlightSecondary,
    tertiary: legacyDefaultHighlightSecondary,
    luxPlus: {
      normalForeground: BLUE[400],
    },
  },
  inverse: {
    primary: {
      normalForeground: TEAL[200],
      normalBackground: TEAL[200],
      normalContrast: GREY[800],
      normalActive: shade(0.24, TEAL[200]),
      normalHover: shade(0.16, TEAL[200]),
      lightForeground: TEAL[50],
      lightBackground: TEAL[50],
      lightContrast: GREY[800],
      darkForeground: TEAL[300],
      darkBackground: TEAL[300],
      darkContrast: GREY[0],
      darkActive: shade(0.24, TEAL[300]),
    },
    secondary: legacyInverseHighlightSecondary,
    tertiary: legacyInverseHighlightSecondary,
    luxPlus: {
      normalForeground: BLUE[400],
    },
  },
}

export const luxuryEscapesDefaultPalette: App.Design.Palette<string> = {
  brand: brandPalette.default,
  neutral: neutralPalette.default,
  product: productPalette.default,
  messaging: messagingPalette.default,
  highlight: highlightPalette.default,
}

export const luxuryEscapesInversePalette: App.Design.Palette<string> = {
  brand: brandPalette.inverse,
  neutral: neutralPalette.inverse,
  product: productPalette.inverse,
  messaging: messagingPalette.inverse,
  highlight: highlightPalette.inverse,
}

export const luxuryEscapesShadows: App.Design.Shadows<string> = { ...baseThemeShadows }
export const luxuryEscapesBorderRadii: App.Design.BorderRadii<string> = {
  ...baseBorderRadii,
  XS: '0px',
  S: '2px',
  M: '4px',
  L: '8px',
  round: '9999px',
}

export const LEGACY_SUISSE_INTL_FONT = '"Suisse Int\'l", "Helvetica Neue", Helvetica, "Noto Sans Arabic", Arial, sans-serif'
export const LEGACY_SUISSE_WORKS_FONT = '"Suisse Works", "Suisse Int\'l", "Helvetica Neue", Helvetica, "Noto Sans Arabic", Arial, sans-serif'
const legacyHeadingMetrics = {
  one: { lineHeight: '34px', lineHeightTablet: '48px', iconSize: '40px' },
  oneAlt: { lineHeight: '34px', lineHeightTablet: '48px', iconSize: '40px' },
  two: { lineHeight: '28px', lineHeightTablet: '34px', iconSize: '40px' },
  twoAlt: { lineHeight: '28px', lineHeightTablet: '34px', iconSize: '40px' },
  three: { lineHeight: '24px', lineHeightTablet: '28px', iconSize: '24px' },
  threeAlt: { lineHeight: '24px', lineHeightTablet: '28px', iconSize: '24px' },
  four: { lineHeight: '21px', lineHeightTablet: '24px', iconSize: '24px' },
  five: { lineHeight: '22px', lineHeightTablet: '24px', iconSize: '20px' },
  six: { lineHeight: '19px', lineHeightTablet: '19px', iconSize: '16px' },
}
export const luxuryEscapesTheme: App.Design.Theme = {
  ...base,
  typography: {
    heading: {
      one: {
        ...legacyHeadingMetrics.one,
        fontFamily: LEGACY_SUISSE_INTL_FONT,
        fontStyle: 'normal',
        fontWeight: '500',
      },
      oneAlt: {
        ...legacyHeadingMetrics.oneAlt,
        fontFamily: LEGACY_SUISSE_WORKS_FONT,
        fontStyle: 'normal',
        fontWeight: '450',
        letterSpacing: '-0.5px',
      },
      two: {
        ...legacyHeadingMetrics.two,
        fontFamily: LEGACY_SUISSE_INTL_FONT,
        fontStyle: 'normal',
        fontWeight: '500',
      },
      twoAlt: {
        ...legacyHeadingMetrics.twoAlt,
        fontFamily: LEGACY_SUISSE_WORKS_FONT,
        fontStyle: 'normal',
        fontWeight: '450',
      },
      three: {
        ...legacyHeadingMetrics.three,
        fontFamily: LEGACY_SUISSE_INTL_FONT,
        fontStyle: 'normal',
        fontWeight: '500',
      },
      threeAlt: {
        ...legacyHeadingMetrics.threeAlt,
        fontFamily: LEGACY_SUISSE_WORKS_FONT,
        fontStyle: 'normal',
        fontWeight: '450',
        letterSpacing: '-0.5px',
      },
      four: {
        ...legacyHeadingMetrics.four,
        fontFamily: LEGACY_SUISSE_INTL_FONT,
        fontStyle: 'normal',
        fontWeight: '500',
      },
      five: {
        ...legacyHeadingMetrics.five,
        fontFamily: LEGACY_SUISSE_INTL_FONT,
        fontStyle: 'normal',
        fontWeight: '600',
      },
      six: {
        ...legacyHeadingMetrics.six,
        fontFamily: LEGACY_SUISSE_INTL_FONT,
        fontStyle: 'normal',
        fontWeight: '600',
      },
    },
  },
  font: {
    primary: {
      provider: 'local',
      localFileSrc: '/fonts/LE-fonts.css',
      name: 'Suisse Int\'l\'',
      family: LEGACY_SUISSE_INTL_FONT,
      fontStyle: 'normal',
      weight: {
        regular: '400',
        medium: '500',
        bold: '500',
        superBold: '600',
      },
    },
    secondary: {
      provider: 'local',
      localFileSrc: '/fonts/LE-fonts.css',
      name: '"Suisse Int\'l\'"',
      family: LEGACY_SUISSE_INTL_FONT,
      fontStyle: 'normal',
      weight: {
        superBold: '600',
        bold: '500',
        medium: '500',
        regular: '400',
      },
    },
    secondaryAlternate: {
      provider: 'local',
      localFileSrc: '/fonts/LE-fonts.css',
      name: 'Suisse Works',
      family: LEGACY_SUISSE_WORKS_FONT,
      fontStyle: 'normal',
      weight: {
        bold: '450',
        medium: '450',
        regular: '450',
        superBold: '450',
      },
    },
  },
}

export default luxuryEscapesTheme
